/**
 * @file Contact-form and in-game-feedback delivery. Writes structured documents to the
 * Firestore `contact` collection; an extension (Trigger Email) on the project listens to
 * this collection and dispatches the actual email. Feedback rows also keep optional
 * `game` and `challenge` context so admins can triage from the Admin.Feedback view.
 *
 * Every public method takes `toast` + `t` so the calling component controls i18n keys
 * and toast styling — this module never reaches into the global i18n or toast singletons.
 *
 * The `html` field is NOT sent: the Trigger Email extension renders the email from
 * `text` alone, and the Firestore rules forbid client-supplied HTML on `/contact`
 * (AUD-26 / AUD-02 — prevents stored-XSS payloads in the admin inbox).
 */

import { addDoc, getDocs, collection } from 'firebase/firestore'
import { db } from '@/main'
import type { Game, Challenge, Feedback, FeedbackMessage } from '@/types/Types'
import { type ToastInterface } from 'vue-toastification'

interface MailService {
  sendContactEmail: (
    subject: string,
    name: string,
    email: string,
    message: string,
    toast: ToastInterface,
    t: (key: string) => string
  ) => void
  sendFeedback: (
    subject: string,
    message: string,
    game: Game | undefined,
    challenge: Challenge | undefined,
    toast: ToastInterface,
    t: (key: string) => string
  ) => void
  getAllFeedbacks: () => Promise<Feedback[]>
}

const mailService: MailService = {
  /** Sends a public contact-form message to `info@allez-allez.app`. Toasts success or error via the provided `t` keys. */
  sendContactEmail: (subject, name, email, message, toast, t) => {
    const contactRef = collection(db, 'contact')
    addDoc(contactRef, {
      to: ['info@allez-allez.app'],
      from: `${name} <${email}>`,
      message: {
        subject: `Contact - ${subject}`,
        text: message
      },
      category: 'contact'
    })
      .then(() => {
        toast.success(t('pages.contact.messages.success'))
      })
      .catch((error) => {
        console.error(error.code, error.message)
        toast.error(t('pages.contact.messages.error'))
      })
  },

  /**
   * In-game feedback submission. Optional `game` and `challenge` arguments are persisted on
   * the contact document so admins can link a feedback entry back to the failing challenge
   * or session. Sent as `category: 'feedback'` to distinguish from public contact form rows.
   */
  sendFeedback: (subject, message, game, challenge, toast, t) => {
    const contactRef = collection(db, 'contact')
    addDoc(contactRef, {
      to: ['info@allez-allez.app'],
      from: `Feedback`,
      message: {
        subject: `Feedback - ${subject}`,
        text: message
      },
      category: 'feedback',
      game: game ?? '',
      challenge: challenge ?? ''
    })
      .then(() => {
        toast.success(t('game.game.feedback.messages.success'))
      })
      .catch((error) => {
        console.error(error.code, error.message)
        toast.error(t('game.game.feedback.messages.error'))
      })
  },

  /** Returns every row in the `contact` collection (both contact-form and feedback entries). Used only by the admin Feedback view. */
  getAllFeedbacks: async () => {
    const feedbacks: Feedback[] = []
    const feedbackRef = collection(db, 'contact')
    const feedbacksSnapshot = await getDocs(feedbackRef)
    feedbacksSnapshot.forEach((doc) => {
      const feedback = {
        id: doc.id,
        feedbackCategory: doc.data().category,
        game: doc.data().game as Game,
        challenge: doc.data().challenge as Challenge,
        message: doc.data().message as FeedbackMessage
      }

      feedbacks.push(feedback)
    })
    return feedbacks
  }
}

export default mailService
